import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import * as api from '../../lib/api';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { DocTypeIcon } from '../../components/ui/DocTypeIcon';
import ButtonSpinner from '../../components/ui/ButtonSpinner';

const DeleteConfirmModal = ({ onConfirm, onCancel }) => {
  const cancelBtnRef = useRef(null);
  useEffect(() => { cancelBtnRef.current?.focus(); }, []);
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center px-4 z-50">
      <div className="modal-panel animate-in">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-red-50 border border-red-200 flex items-center justify-center mb-4">
            <SafeIcon icon={FiIcons.FiTrash2} className="text-2xl text-red-500" />
          </div>
          <h3 className="text-lg font-bold text-brand-navy mb-2">Discard Document?</h3>
          <p className="text-sm text-slate-600 leading-relaxed">Are you sure? This action cannot be undone.</p>
        </div>
        <div className="flex gap-3">
          <button ref={cancelBtnRef} onClick={onCancel} className="btn-secondary flex-1">Cancel</button>
          <button
            onClick={onConfirm}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md transition-colors font-medium flex items-center justify-center gap-2"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const EmptyState = ({ onStart }) => (
  <div className="flex flex-col items-center justify-center py-20 px-6">
    <div className="relative mb-6">
      <div className="w-20 h-20 rounded-2xl bg-brand-navy flex items-center justify-center shadow-lg">
        <SafeIcon icon={FiIcons.FiFileText} className="text-4xl text-white" />
      </div>
      <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-brand-gold flex items-center justify-center shadow">
        <SafeIcon icon={FiIcons.FiShield} className="text-white text-xs" />
      </div>
    </div>
    <h2 className="text-2xl font-bold text-brand-navy text-center mb-3">Your legacy plan starts here.</h2>
    <p className="text-slate-500 text-center max-w-sm mb-8 leading-relaxed">
      Protect your family and assets in minutes. Create your first legal document with step-by-step guidance from licensed advisors.
    </p>
    <button
      onClick={onStart}
      className="relative inline-flex items-center gap-2.5 px-7 py-3 rounded-lg font-semibold text-white text-sm shadow-md transition-all hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
      style={{ backgroundColor: '#0f172a' }}
    >
      <span className="absolute inset-0 rounded-lg opacity-20" style={{ background: 'linear-gradient(135deg,#d97706 0%,transparent 60%)' }} />
      <SafeIcon icon={FiIcons.FiPlus} className="text-brand-gold text-base relative z-10" />
      <span className="relative z-10">Create Your First Document</span>
    </button>
    <p className="mt-5 text-xs text-slate-400 flex items-center gap-1.5">
      <SafeIcon icon={FiIcons.FiLock} className="text-slate-300" />
      Secure, private, and advisor-reviewed
    </p>
  </div>
);

export const ClientDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [startingDocType, setStartingDocType] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => { loadDocs(); }, [user.id]);

  const loadDocs = async () => {
    setFetchError(null);
    setLoading(true);
    try {
      const docs = await api.getClientDocuments(user.id);
      setDocuments(docs);
    } catch (err) {
      console.error(err);
      setFetchError('Could not load your documents. Please try again.');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStartDoc = async (type) => {
    setStartingDocType(type);
    try {
      const newDoc = await api.createDocument(user.id, type);
      navigate(`/client/document/${newDoc.id}`);
    } catch (err) {
      console.error(err);
      setFetchError('Could not create the document. Please try again.');
    } finally {
      setStartingDocType(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setFetchError(null);
    try {
      await api.deleteDocument(deleteTarget);
      setDocuments((prev) => prev.filter((d) => d.id !== deleteTarget));
    } catch (err) {
      console.error(err);
      setFetchError('Could not discard this document. Please try again.');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const docTypes = {
    will: 'Last Will & Testament',
    trust: 'Revocable Living Trust',
    immigration_poa: 'Special Power of Attorney',
  };

  if (loading) return <div className="p-8 text-center text-slate-500">Loading your vault...</div>;

  return (
    <div className="space-y-6">
      {fetchError && (
        <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-lg p-3">
          <SafeIcon icon={FiIcons.FiAlertCircle} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{fetchError}</p>
        </div>
      )}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="page-title">My Documents</h1>
          <p className="page-subtitle">Manage your estate planning documents</p>
        </div>
        {documents.length > 0 && (
          <button onClick={() => setShowNewModal(true)} className="btn-primary w-full sm:w-auto">
            <SafeIcon icon={FiIcons.FiPlus} /> Start New Document
          </button>
        )}
      </div>

      {documents.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <EmptyState onStart={() => setShowNewModal(true)} />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {documents.map((doc) => (
            <div key={doc.id} className="card hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <DocTypeIcon type={doc.type} />
                <StatusBadge status={doc.status} />
              </div>
              <h3 className="font-semibold text-lg text-brand-navy mb-1">{docTypes[doc.type]}</h3>
              <p className="text-sm text-slate-500 mb-6">
                Last updated {new Date(doc.updated_at).toLocaleDateString()}
              </p>
              <div className="pt-4 border-t border-slate-100 flex justify-between items-center">
                <button
                  onClick={() => setDeleteTarget(doc.id)}
                  className="text-slate-400 hover:text-red-500 flex items-center gap-1.5 text-sm transition-colors"
                  title="Discard document"
                >
                  <SafeIcon icon={FiIcons.FiTrash2} className="text-base" /> Discard
                </button>
                <button
                  onClick={() => navigate(`/client/document/${doc.id}`)}
                  className="text-brand-navy font-medium hover:text-brand-gold flex items-center gap-2 text-sm transition-colors"
                >
                  {doc.status === 'draft' ? 'Continue Editing' : 'View Details'}
                  <SafeIcon icon={FiIcons.FiArrowRight} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showNewModal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center px-4 z-50">
          <div className="modal-panel">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-brand-navy">Select Document Type</h3>
              <button onClick={() => setShowNewModal(false)} disabled={!!startingDocType} className="text-slate-400 hover:text-slate-600 disabled:opacity-40">
                <SafeIcon icon={FiIcons.FiX} className="text-xl" />
              </button>
            </div>
            <div className="space-y-3">
              {Object.entries(docTypes).map(([type, label]) => {
                const isThisLoading = startingDocType === type;
                const isAnyLoading = !!startingDocType;
                return (
                  <button
                    key={type}
                    onClick={() => handleStartDoc(type)}
                    disabled={isAnyLoading}
                    className="w-full flex items-center gap-4 p-4 rounded-lg border border-slate-200 hover:border-brand-navy hover:bg-slate-50 transition-all text-left group disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <DocTypeIcon type={type} className="text-xl" />
                    <span className="font-medium text-slate-700 group-hover:text-brand-navy flex-1">{label}</span>
                    {isThisLoading && <ButtonSpinner />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <DeleteConfirmModal onConfirm={handleDeleteConfirm} onCancel={() => setDeleteTarget(null)} />
      )}
    </div>
  );
};